// ============================================
// services/api.js - Cliente HTTP para API
// Scriptus Pharma Partner
// ============================================

import axios from 'axios';
import { auth } from '../config/firebase';

// ✅ URL base da API (sem /api/v1, pois já está nas rotas)
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.scriptusrxia.com.br';

// Cria instância do axios
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(async (config) => {
  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('Erro ao obter token:', error);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Se token expirou, tenta renovar
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken(true); // force refresh
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Erro ao renovar token:', refreshError);
        // Redireciona para login se não conseguir renovar
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// ============================================
// API de Autenticação de Farmácia
// ============================================
export const authApi = {
  // Registro de nova farmácia
  register: (data) => api.post('/api/v1/auth/pharmacy/register', data),
  
  // Login (Firebase Auth + verificação no backend)
  login: (data) => api.post('/api/v1/auth/pharmacy/login', data),
  
  // Perfil da farmácia
  getProfile: () => api.get('/api/v1/auth/pharmacy/profile'),
  updateProfile: (data) => api.put('/api/v1/auth/pharmacy/profile', data),
};

// ============================================
// API de Dispensação
// ============================================
export const dispensationApi = {
  // Validar token/QR Code da prescrição
  validateToken: (token) => api.get(`/api/v1/dispensation/validate/${token}`),
  
  // Realizar dispensação
  dispense: (data) => api.post('/api/v1/dispensation/dispense', data),
  
  // Histórico de dispensações
  getHistory: (params) => api.get('/api/v1/dispensation/history', { params }),
  
  // Detalhes de uma dispensação
  getDetails: (id) => api.get(`/api/v1/dispensation/${id}`),
};

// ============================================
// API de Inventário (Plano Partner)
// ============================================
export const inventoryApi = {
  // Listar produtos
  getAll: () => api.get('/api/v1/inventory'),
  
  // Buscar produto específico
  getById: (id) => api.get(`/api/v1/inventory/${id}`),
  
  // Adicionar produto
  create: (data) => api.post('/api/v1/inventory', data),
  
  // Atualizar produto
  update: (id, data) => api.put(`/api/v1/inventory/${id}`, data),
  
  // Remover produto
  delete: (id) => api.delete(`/api/v1/inventory/${id}`),
  
  // Atualizar estoque em lote
  bulkUpdate: (items) => api.patch('/api/v1/inventory/bulk', { items }),
};

// ============================================
// API de Pagamento/Assinatura
// ============================================
export const paymentApi = {
  // Criar sessão de checkout para upgrade
  createCheckout: (priceId) => api.post('/api/v1/payment/pharmacy/checkout', { priceId }),
  
  // Criar portal do cliente (gerenciar assinatura)
  createPortal: () => api.post('/api/v1/payment/pharmacy/portal'),
  
  // Status da assinatura
  getSubscriptionStatus: () => api.get('/api/v1/payment/pharmacy/status'),
};

// ============================================
// API de Marketplace
// ============================================
export const marketApi = {
  // Buscar medicamentos por região
  search: (params) => api.get('/api/v1/market/search', { params }),
  
  // Perfil público da farmácia
  getPublicProfile: (pharmacyId) => api.get(`/api/v1/market/pharmacy/${pharmacyId}`),
};

// ============================================
// API Pública (Validação de Prescrição)
// Não requer autenticação
// ============================================
export const publicApi = {
  // Validar prescrição pelo ID (QR Code)
  validatePrescription: (prescricaoId) => 
    axios.get(`${API_BASE_URL}/api/validar-prescricao/${prescricaoId}`),
  
  // Download do PDF
  downloadPdf: (prescricaoId) => 
    axios.get(`${API_BASE_URL}/api/validar-prescricao/${prescricaoId}/pdf`, {
      responseType: 'blob'
    }),
  
  // Verificar integridade
  verifyIntegrity: (prescricaoId) => 
    axios.get(`${API_BASE_URL}/api/validar-prescricao/${prescricaoId}/verificar-integridade`),
};

// ============================================
// COMPATIBILIDADE: pharmacyApi (usado pelas páginas existentes)
// ============================================
export const pharmacyApi = {
  // Registro e Auth
  register: (data) => api.post('/api/v1/auth/pharmacy/register', data),
  login: (data) => api.post('/api/v1/auth/pharmacy/login', data),
  
  // Perfil
  getProfile: () => api.get('/api/v1/auth/pharmacy/profile'),
  updateProfile: (data) => api.put('/api/v1/auth/pharmacy/profile', data),
  
  // Dispensação
  validateToken: (token) => api.get(`/api/v1/dispensation/validate/${token}`),
  dispense: (data) => api.post('/api/v1/dispensation/dispense', data),
  getHistory: (params) => api.get('/api/v1/dispensation/history', { params }),
  getDispensationDetails: (id) => api.get(`/api/v1/dispensation/${id}`),
  
  // Inventário
  getInventory: () => api.get('/api/v1/inventory'),
  addProduct: (data) => api.post('/api/v1/inventory', data),
  updateProduct: (id, data) => api.put(`/api/v1/inventory/${id}`, data),
  deleteProduct: (id) => api.delete(`/api/v1/inventory/${id}`),
  
  // Pagamento
  createCheckout: (priceId) => api.post('/api/v1/payment/pharmacy/checkout', { priceId }),
  createPortal: () => api.post('/api/v1/payment/pharmacy/portal'),
  getSubscriptionStatus: () => api.get('/api/v1/payment/pharmacy/status'),
  
  // Marketplace
  searchMarket: (params) => api.get('/api/v1/market/search', { params }),
};

// ============================================
// Export padrão com todas as APIs
// ============================================
export default {
  auth: authApi,
  dispensation: dispensationApi,
  inventory: inventoryApi,
  payment: paymentApi,
  market: marketApi,
  public: publicApi,
  pharmacy: pharmacyApi, // Alias para compatibilidade
};